import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";

export default function Privacy() {
  return (
    <div className="pt-24 pb-16 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="privacy-page">
      <SEO
        title="Privacy Policy"
        description="Serverlistr Privacy Policy — what we collect, why, and your rights."
        path="/privacy"
      />
      <Breadcrumbs items={[{ name: "Privacy Policy" }]} />
      <h1 className="font-heading text-3xl sm:text-4xl font-black">Privacy Policy</h1>
      <p className="mt-2 text-xs text-zinc-500 font-mono">Last updated: {new Date().toISOString().slice(0, 10)}</p>

      <div className="mt-8 space-y-8 text-sm text-zinc-300 leading-relaxed">
        <Section title="1. Overview">
          Serverlistr respects your privacy. This policy explains what data we collect,
          why, and what you can do about it. We do not sell your data to anyone, ever.
        </Section>

        <Section title="2. What we collect">
          When you sign in with Discord we receive:
          <ul className="mt-2 list-disc list-inside space-y-1 text-zinc-400">
            <li>Your Discord user ID</li>
            <li>Your Discord username</li>
            <li>Your email address (as attached to your Discord account)</li>
            <li>Your avatar URL</li>
          </ul>
          When you add the Serverlistr bot to a server we receive:
          <ul className="mt-2 list-disc list-inside space-y-1 text-zinc-400">
            <li>The guild (server) ID</li>
            <li>Public server metadata (name, icon, banner, approximate member/online counts)</li>
          </ul>
          When you use the site we log:
          <ul className="mt-2 list-disc list-inside space-y-1 text-zinc-400">
            <li>IP address (for rate-limiting and abuse prevention only)</li>
            <li>Session cookie for keeping you signed in (HttpOnly, Secure)</li>
            <li>Ratings and reviews you post, and listings you submit</li>
          </ul>
          If you buy a Featured slot, Stripe collects payment details directly under its own
          privacy policy. Serverlistr only stores a Stripe session ID, the amount, and the
          package purchased — never your card number.
        </Section>

        <Section title="3. What we don’t collect">
          <ul className="mt-2 list-disc list-inside space-y-1 text-zinc-400">
            <li>Messages inside your Discord server (the bot has no privileged intents)</li>
            <li>Membership rosters (we only see approximate counts)</li>
            <li>Card numbers, CVCs, or bank details</li>
            <li>Data from any Discord server that has NOT added our bot</li>
          </ul>
        </Section>

        <Section title="4. How we use it">
          <ul className="mt-2 list-disc list-inside space-y-1 text-zinc-400">
            <li>To authenticate you and keep you signed in</li>
            <li>To display your listing publicly on the directory</li>
            <li>To auto-refresh public server metadata (member counts, icons) every 20 minutes</li>
            <li>To detect and prevent abuse (spam, brute force, duplicate votes)</li>
            <li>To process one-time payments for Featured slots (via Stripe)</li>
          </ul>
        </Section>

        <Section title="5. Cookies">
          We use one HttpOnly, Secure cookie called <span className="font-mono text-white">session_token</span>
          &nbsp;to keep you signed in. It expires after 7 days. We do not use tracking or
          advertising cookies.
        </Section>

        <Section title="6. Sharing">
          We share data only with:
          <ul className="mt-2 list-disc list-inside space-y-1 text-zinc-400">
            <li><span className="font-semibold text-white">Stripe</span> — to process payments</li>
            <li><span className="font-semibold text-white">Discord</span> — to authenticate you and fetch public server data</li>
            <li>Law enforcement, if legally compelled</li>
          </ul>
          We do not sell, rent, or trade your data to advertisers or data brokers.
        </Section>

        <Section title="7. Data retention">
          We keep your account and listings until you request deletion. Payment records
          are kept for 7 years as required by law. Server session logs are kept for 90 days
          for abuse investigations, then deleted.
        </Section>

        <Section title="8. Your rights">
          You have the right to:
          <ul className="mt-2 list-disc list-inside space-y-1 text-zinc-400">
            <li>Access the data we hold about you</li>
            <li>Correct inaccurate data</li>
            <li>Delete your account and all associated data</li>
            <li>Withdraw consent for future data processing</li>
            <li>Lodge a complaint with your local data protection authority</li>
          </ul>
          To exercise any of these rights, message us in our{" "}
          <a
            href="https://discord.gg/HWQGPR6nR4"
            target="_blank"
            rel="noreferrer"
            className="text-blurple hover:text-blurple-hover"
          >
            support Discord
          </a>
          .
        </Section>

        <Section title="9. Children">
          Serverlistr is not intended for users under 13. If we learn a user is under 13,
          we delete their account immediately.
        </Section>

        <Section title="10. International users">
          We may store data in the United States or Europe. By using Serverlistr you consent
          to this transfer. We use appropriate technical safeguards regardless of location.
        </Section>

        <Section title="11. Changes">
          We may update this policy. Material changes will be shown by updating the
          &quot;Last updated&quot; date at the top and highlighted in a notice on the site.
        </Section>

        <Section title="12. Contact">
          Questions? Join our
          <a
            href="https://discord.gg/HWQGPR6nR4"
            target="_blank"
            rel="noreferrer"
            className="text-blurple hover:text-blurple-hover ml-1"
          >
            support Discord
          </a>
          .
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-heading font-bold text-lg text-white mb-3">{title}</h2>
      <div>{children}</div>
    </section>
  );
}
